import {
  Grid,
  LinearProgress,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  FormControl,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import EditGradeForm from "./EditGradeForm";
import { useGetGradesQuery, useDeleteGradeMutation, useCopyGradeSubsectionMutation } from "./gradesApiSlice";
import { useGetSubSectionQuery, useGetSubSectionsPaginatedQuery } from "../sections/sectionsApiSlice";
import CreateGradeForm from "./CreateGradeForm";
import toast from "../../../../helpers/toast";
import CustomWarningDialog from "../../../customComponents/CustomWarningDialog";
import { StyledTextField } from "../../../customComponents/styled/styledInputs";

function Grades() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [focusedItem, setFocusedItem] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [subsectionToCopy, setSubsectionToCopy] = useState(null);
  const [targetSearch, setTargetSearch] = useState("");
  const [targetSubsectionId, setTargetSubsectionId] = useState(null);

  const { data: subsections, isLoading: subsectionsLoading } = useGetSubSectionQuery("all");
  const { data: grades, isLoading: getGradesLoading } = useGetGradesQuery("all");
  const [deleteGrade] = useDeleteGradeMutation();
  const [copyGradeSubsection, { isLoading: copyLoading }] = useCopyGradeSubsectionMutation();

  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery(
    { page: 1, per_page: 20, q: targetSearch },
    { skip: !openCopyDialog }
  );
  const targetSubsections = subsectionsData?.subsections ?? [];

  const handleDelete = async (item) => {
    try {
      const response = await deleteGrade(item).unwrap();
      if (response.message) {
        toast.success("Grade deleted successfully");
      } else {
        toast.error("Grade not deleted");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleCopy = async () => {
    if (!subsectionToCopy || !targetSubsectionId) return;
    try {
      const response = await copyGradeSubsection({
        source_subsection_id: subsectionToCopy.id,
        target_subsection_id: targetSubsectionId,
      }).unwrap();
      toast.success(response.message || "Grades copied successfully");
      closeCopyDialog();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to copy grades");
    }
  };

  const closeCopyDialog = () => {
    setOpenCopyDialog(false);
    setSubsectionToCopy(null);
    setTargetSubsectionId(null);
    setTargetSearch("");
  };

  return (
    <>
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={itemToDelete}
        action={handleDelete}
        message="Are you sure you want to delete this grade?"
      />
      <Dialog open={openCopyDialog} onClose={closeCopyDialog} fullWidth maxWidth="xs">
        <DialogTitle>Copy grades from "{subsectionToCopy?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the target subsection. All grades from "{subsectionToCopy?.name}" will be copied into it.
          </Typography>
          <FormControl fullWidth>
            <Autocomplete
              options={targetSubsections.filter((s) => s.id !== subsectionToCopy?.id)}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={targetSubsections.find((s) => s.id === targetSubsectionId) || null}
              onChange={(_, value) => setTargetSubsectionId(value?.id || null)}
              onInputChange={(_, value, reason) => { if (reason === "input") setTargetSearch(value); }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <StyledTextField {...params} placeholder="Search subsection..." variant="outlined" />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCopyDialog}>Cancel</Button>
          <Button onClick={handleCopy} disabled={!targetSubsectionId || copyLoading} variant="contained">
            {copyLoading ? "Copying..." : "Copy All"}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container columnSpacing={3}>
        <Grid item xs={12} lg={4}>
          <StyledPaper>
            {focusedItem ? (
              <EditGradeForm
                item={focusedItem}
                setItem={setFocusedItem}
                subsections={subsections}
              />
            ) : (
              <CreateGradeForm />
            )}
          </StyledPaper>
        </Grid>
        <Grid item xs={12} lg={8}>
          {(subsectionsLoading || getGradesLoading) && <LinearProgress sx={{ mb: 1 }} />}
          {subsections?.map((item) => (
            <StyledPaper key={item.id}>
              {getGradesLoading && <LinearProgress />}
              <Box className="paperHeader" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="formHeading">
                  {item.name} Grades
                </Typography>
                <ContentCopyIcon
                  className="tableCellLink"
                  sx={{ fontSize: 18, color: "text.secondary" }}
                  titleAccess="Copy all grades in this subsection"
                  onClick={() => {
                    setSubsectionToCopy(item);
                    setTargetSubsectionId(null);
                    setTargetSearch("");
                    setOpenCopyDialog(true);
                  }}
                />
              </Box>
              {isMobile ? (
                <Box sx={{ p: 2 }}>
                  {grades
                    ?.filter((grade) => grade.subsection_id === item.id)
                    .map((row) => (
                      <Box key={row.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                        <Typography sx={{ color: "primary.main", fontWeight: 600 }}>{row.name}</Typography>
                        <Typography variant="body2" color="text.secondary">Type: {row.type}</Typography>
                        <Typography variant="body2" color="text.secondary">Min score: {row.min_score ?? "—"}</Typography>
                        <Typography variant="body2" color="text.secondary">Max score: {row.max_score ?? "—"}</Typography>
                        <Typography variant="body2" color="text.secondary">Description: {row.description || "-"}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <SvgIcon sx={{ color: "primary.safeOnLight", cursor: "pointer" }} component={EditIcon} inheritViewBox onClick={() => { setFocusedItem(row); }} />
                          <DeleteIcon onClick={() => { setItemToDelete(row); setOpenWarning(true); }} sx={{ color: "primary.safeOnLight", cursor: "pointer" }} />
                        </Stack>
                      </Box>
                    ))}
                </Box>
              ) : (
                <TableContainer sx={{ pb: 4 }}>
                  <Table sx={{ width: "100%", boxShadow: "none" }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Grade</StyledTableCell>
                        <StyledTableCell>type</StyledTableCell>
                        <StyledTableCell>Min Score</StyledTableCell>
                        <StyledTableCell>Max Score</StyledTableCell>
                        <StyledTableCell align="left">description</StyledTableCell>
                        <StyledTableCell align="left">edit</StyledTableCell>
                        <StyledTableCell align="left">delete</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grades
                        ?.filter((grade) => grade.subsection_id === item.id)
                        .map((row) => (
                          <StyledTableRow key={row.id}>
                            <StyledTableCell component="th" scope="row">
                              {row.name}
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              {row.type}
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              {row.min_score ?? "—"}
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              {row.max_score ?? "—"}
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              {row.description}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <SvgIcon
                                sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                                component={EditIcon}
                                inheritViewBox
                                onClick={() => { setFocusedItem(row); }}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <DeleteIcon
                                onClick={() => { setItemToDelete(row); setOpenWarning(true); }}
                                sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </StyledPaper>
          ))}
        </Grid>
      </Grid>
    </>
  );
}

export default Grades;
